/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Use global debounce from mm_utils.js

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		// timestamp uniquement en dev : toLocaleString('fr-FR') est coûteux et ne sert qu'au logging dev
		timestamp: window.isDev ? (window.getTimestamp?.() || '') : null,
		event,
		data: detail,
		source: 'cart'
	};
	
	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },
	
	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),
	
	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		// Timeout dur (apiTimeout) : sans lui, un fetch sur reseau suspendu laisse les boutons
		// desactives jusqu'au timeout natif du navigateur (plusieurs minutes). L'AbortError
		// tombe dans le catch existant -> cart:api-error + restauration UI par le finally appelant.
		const config = { method, signal: AbortSignal.timeout(CART_CONFIG.apiTimeout) };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// cart/add.js renvoie le(s) line item(s), pas le panier : sans item_count, on relit cart.js
		if (typeof data.item_count === 'number') {
			const count = data.item_count;
			DOM.cartBubbles.forEach(bubble => {
				bubble.classList.toggle('mm-none', count === 0);
				bubble.textContent = count || '';
			});
			cartEmit('count-updated', { count });
		} else {
			this.request('GET', 'cart.js').catch(error => window.log('error', 'Cart', 'Failed to refresh cart count', { error: error.message }));
		}

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;
		
		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);
		
		if (newCart && currentCart) {
			// Detruire les instances Splide existantes AVANT de remplacer le HTML : sinon
			// innerHTML jette les nœuds sans .destroy() (fuite de listeners internes Splide).
			if (typeof Splide !== 'undefined') {
				currentCart.querySelectorAll('.mm-drawer-cart .splide, .splide').forEach(function(element) {
					if (element._splide) {
						try { element._splide.destroy(true); } catch (error) {
							window.log('warn', 'Cart', 'Failed to destroy Splide:', { error });
						}
					}
				});
			}

			// Selection d'un cadeau => refreshDOM : on FERME le panneau (retour au panier
			// avec le cadeau ajoute). Le nouveau HTML rend le panneau ferme (aria-hidden,
			// sans --open) ; on reinitialise juste l'etat + l'inert cote JS avant le remplacement.
			const wasGiftPanelOpen = Cart.giftPanel.openBlockId !== null;
			if (wasGiftPanelOpen) {
				Cart.giftPanel.openBlockId = null;
				Cart.giftPanel.trigger = null;
			}

			currentCart.innerHTML = newCart.innerHTML;

			// Re-monter les sliders sur les nouveaux nœuds
			if (typeof Splide !== 'undefined') {
				currentCart.querySelectorAll('.splide').forEach(function(element) {
					if (!element._splide) {
						try {
							// Stocker l'instance : la garde destroy ci-dessus et l'anti-double-init
							// de SplideManager reposent sur element._splide.
							element._splide = new Splide(element).mount();
						} catch (error) {
							window.log('warn', 'Cart', 'Failed to initialize Splide:', { element, error });
						}
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}

			// Le panneau etait ouvert (selection d'un cadeau) : le contenu du drawer a ete
			// remplace (inert perdu avec les anciens nœuds), le nouveau panneau est ferme.
			// On re-arme le focus trap sur le drawer entier et on focalise le 1er element.
			if (wasGiftPanelOpen) {
				Cart.setupFocusTrap(DOM.cartWrapper, null);
			}

			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		// Retire un trap encore actif (double open sans close) : sinon la reference
		// handleKeyDown est ecrasee et l'ancien listener devient impossible a retirer.
		this.removeFocusTrap();

		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape : ferme d'abord le panneau cadeau s'il est ouvert (2 niveaux),
			// sinon ferme le panier.
			if (e.key === 'Escape') {
				e.preventDefault();
				if (this.giftPanel.openBlockId) {
					this.closeGiftPanel();
					return;
				}
				this.close();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		window.log('info', 'Cart', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'Cart', 'Focus trap removed');
		}
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement, skipFetch = false) {
		if (!cartEmit('before-open', {}, true)) return;

		// skipFetch : evite un GET cart.js redondant quand l'appelant a deja l'etat a jour
		// (ex: apres un POST cart/add.js qui renvoie deja le panier + les sections).
		if (!skipFetch) {
			this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));
		}

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.classList.add('mm-dc-is-open');
			html.classList.add('stop-scrolling');

			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, header, footer').forEach(el => {
				if (!wrapper.contains(el)) el.inert = true;
			});

			// Hide overlays
			['shopify-chat', 'axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Fallback to active element so focus is always restored on close (WCAG 2.4.3)
			const trigger = triggerElement || document.activeElement;
			this.setupFocusTrap(wrapper, trigger);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper) wrapper.classList.remove('mm-dc-is-open');
		if (html) html.classList.remove('stop-scrolling');

		// Fermer un panneau cadeau eventuellement ouvert (reset etat, retire inert/aria)
		this.closeGiftPanel({ restoreFocus: false });

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);

		// Remove focus trap
		this.removeFocusTrap();

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	// Etat du panneau cadeau lateral (choix multi-produits).
	giftPanel: {
		openBlockId: null,
		trigger: null
	},

	/**
	 * Ouvre le panneau cadeau d'un bloc (glisse par-dessus le panier).
	 * Le contenu du drawer sous le panneau passe inert : le focus reste piege
	 * dans le panneau (les elements inert ne sont pas focusables), et le focus
	 * trap global du drawer continue de gerer Tab sur les focusables restants.
	 */
	openGiftPanel(blockId, trigger) {
		const drawer = DOM.cart?.querySelector(CART_CONFIG.selectors.drawerCart) || document.querySelector(CART_CONFIG.selectors.drawerCart);
		const panel = document.querySelector(`[data-gift-panel="${blockId}"]`);
		if (!drawer || !panel) return;

		// Rendre inert tout le contenu du drawer sauf le panneau lui-meme.
		Array.from(drawer.children).forEach(child => {
			if (child !== panel) child.inert = true;
		});

		panel.classList.add('mm-gift-panel--open');
		panel.setAttribute('aria-hidden', 'false');

		this.giftPanel.openBlockId = blockId;
		this.giftPanel.trigger = trigger || document.activeElement;

		// Trap scope au PANNEAU (pas au wrapper) : querySelectorAll ne filtre pas les
		// elements inert, donc scoper au wrapper focaliserait un bouton inert (echec
		// silencieux du .focus()). Le panneau ne contient que des focusables actifs.
		this.setupFocusTrap(panel, this.giftPanel.trigger);

		window.emit('global:gift-panel-opened', { blockId });
	},

	/**
	 * Ferme le panneau cadeau. restoreFocus=false quand appele depuis close()
	 * (le panier se ferme, inutile de rendre le focus au trigger interne).
	 */
	closeGiftPanel(options) {
		const restoreFocus = !options || options.restoreFocus !== false;
		const blockId = this.giftPanel.openBlockId;
		if (!blockId) return;

		const drawer = DOM.cart?.querySelector(CART_CONFIG.selectors.drawerCart) || document.querySelector(CART_CONFIG.selectors.drawerCart);
		const panel = document.querySelector(`[data-gift-panel="${blockId}"]`);

		if (panel) {
			panel.classList.remove('mm-gift-panel--open');
			panel.setAttribute('aria-hidden', 'true');
		}
		if (drawer) {
			Array.from(drawer.children).forEach(child => { child.inert = false; });
		}

		const trigger = this.giftPanel.trigger;
		this.giftPanel.openBlockId = null;
		this.giftPanel.trigger = null;

		// Le panier reste ouvert : re-arme le trap sur l'ensemble du drawer.
		// setupFocusTrap refocalise firstFocusable via setTimeout(100) ; on force le
		// retour sur le trigger APRES ce delai pour ne pas se faire ecraser.
		if (DOM.cartWrapper?.classList.contains('mm-dc-is-open') && restoreFocus) {
			this.setupFocusTrap(DOM.cartWrapper, trigger);
			if (trigger && document.contains(trigger)) {
				setTimeout(() => trigger.focus(), 120);
			}
		}

		window.emit('global:gift-panel-closed', { blockId });
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;
		
		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;
		
		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;
		
		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });
		
		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
			
			// Shopify cart/change.js requires 1-based line index and proper sections format
			const requestBody = {
				line: parseInt(lineIndex, 10), // Should be 1-based from template
				quantity: newQty
			};
			
			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}
			
			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);
			
			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			// Rollback visuel : re-fetch AVEC sections pour que refreshDOM resynchronise les
			// lignes du drawer (un GET sans sections ne met a jour que la bulle compteur,
			// laissant la quantite optimiste affichee alors que le serveur l'a refusee).
			const rollbackSectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
			const rollbackPath = rollbackSectionId ? `cart.js?sections=${rollbackSectionId}` : 'cart.js';
			CartAPI.request('GET', rollbackPath).catch(err => window.log('error', 'Cart', 'Rollback fetch failed', { error: err.message }));
			throw error;
		}
	},

	/**
	 * Free product decline flags (localStorage) — refus persistant du cadeau.
	 * Cle : mm_declined_free_<variantId>. Empeche le reajout auto d'un cadeau
	 * retire manuellement, tant que le panier reste au-dessus du seuil.
	 */
	_declinedKey(variantId) {
		return 'mm_declined_free_' + variantId;
	},

	isFreeProductDeclined(variantId) {
		try {
			return localStorage.getItem(this._declinedKey(variantId)) === 'true';
		} catch (e) {
			return false;
		}
	},

	declineFreeProduct(variantId) {
		try {
			localStorage.setItem(this._declinedKey(variantId), 'true');
		} catch (e) {
			window.log('warn', 'Cart', 'localStorage indisponible (decline)', { error: e.message });
		}
	},

	clearDeclinedFreeProduct(variantId) {
		try {
			localStorage.removeItem(this._declinedKey(variantId));
		} catch (e) {
			/* no-op */
		}
	},

	/**
	 * Process free products
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		if (!data.sections?.[sectionId]) return data;

		const temp = document.createElement('div');
		temp.innerHTML = data.sections[sectionId];
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);
		
		if (!freeProductsEl?.children.length) return data;

		const updates = {};
		Array.from(freeProductsEl.children).forEach(el => {
			const variantId = el.dataset.variantId;
			const shouldHave = el.dataset.freeProduct === 'true';
			// mode 'auto' (1 produit) = ajout automatique. mode 'multi' (>1) = choix via slider,
			// pas d'auto-add : l'utilisateur selectionne son cadeau. Defaut 'auto' (retro-compat).
			const mode = el.dataset.fpMode || 'auto';
			const hasIt = !!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId));

			if (hasIt && !shouldHave) {
				// Retrait : s'applique aux deux modes (panier repasse sous le seuil → on retire le cadeau).
				updates[variantId] = 0;
				cartEmit('free-product-removing', { variantId });
			} else if (!hasIt && shouldHave && mode === 'auto') {
				// Ajout automatique : uniquement en mode 'auto'. Refus persistant : ne pas reajouter
				// un cadeau retire manuellement tant que le panier reste au-dessus du seuil (localStorage).
				if (Cart.isFreeProductDeclined(variantId)) return;
				updates[variantId] = 1;
				cartEmit('free-product-adding', { variantId });
			}

			// Seuil non atteint : le cadeau redevient proposable → on nettoie le refus.
			if (!shouldHave) Cart.clearDeclinedFreeProduct(variantId);
		});

		// Un seul cadeau par bloc multi : si l'utilisateur choisit un 2e produit du meme bloc
		// (property _free_offer identique), on retire les precedents et on ne garde que celui
		// qui vient d'etre ajoute.
		// data.items n'existe QUE sur les reponses cart/change.js|update.js : cart/add.js
		// renvoie le line item ajoute (pas le panier). On fetch alors /cart.js pour avoir
		// la liste complete avec les properties _free_offer, sinon la dedup ne s'applique jamais.
		let items = Array.isArray(data.items) ? data.items : [];
		if (items.length === 0) {
			try {
				const fullCart = await CartAPI.request('GET', 'cart.js');
				items = Array.isArray(fullCart.items) ? fullCart.items : [];
			} catch (error) {
				window.log('warn', 'Cart', 'Dedup free products: cart.js fetch failed', { error: error.message });
			}
		}
		// Le variant qu'on vient d'ajouter (reponse cart/add.js) est celui a CONSERVER.
		// Deterministe, contrairement a "garder le dernier de l'ordre panier" (l'ordre
		// retourne par cart.js apres un ajout n'est pas garanti -> mauvais cadeau conserve).
		const justAddedVariantId = data && data.variant_id ? data.variant_id : null;
		const giftsByBlock = {};
		items.forEach(item => {
			const offerId = item.properties && item.properties._free_offer;
			if (!offerId) return;
			(giftsByBlock[offerId] = giftsByBlock[offerId] || []).push(item.variant_id);
		});
		Object.values(giftsByBlock).forEach(variantIds => {
			if (variantIds.length < 2) return;
			// Determiner le cadeau a garder : celui qu'on vient d'ajouter s'il est dans ce
			// bloc, sinon le dernier de la liste (fallback pour les cas hors ajout direct).
			let keep = variantIds[variantIds.length - 1];
			if (justAddedVariantId && variantIds.indexOf(justAddedVariantId) !== -1) {
				keep = justAddedVariantId;
			}
			variantIds.forEach(variantId => {
				if (variantId === keep) return;
				updates[variantId] = 0;
				cartEmit('free-product-removing', { variantId });
			});
		});

		if (Object.keys(updates).length > 0) {
			try {
				// Shopify cart/update.js expects updates object and optional sections
				const requestBody = { updates };
				
				if (sectionId) {
					requestBody.sections = sectionId;
				}
				
				await CartAPI.request('POST', 'cart/update.js', requestBody);
				cartEmit('free-products-updated', { updates });
			} catch (error) {
				cartEmit('free-products-error', { error: error.message });
			}
		}

		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = window.debounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;
	
	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;
	
	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};
		
		if (!cartEmit('before-item-remove', removeEventData, true)) return;
		
		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		debouncedUpdate(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
/**
 * Sets product quantity to an exact value (utilise par le select qty de la page panier).
 * Clamp entre 0 et maxQuantity ; emet les cart:*-quantity-* events adequats et declenche
 * la mise a jour AJAX. Passe par la meme sequence d'events que les +/- pour rester coherent
 * avec les listeners externes (pont Shopify Standard Events, analytics, etc.).
 */
window.mmSetProductQuantity = (lineIndex, quantity) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const requested = parseInt(quantity, 10);
	if (isNaN(requested) || requested < 0) return;

	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = Math.min(requested, max);

	if (newQty === current) return;

	if (newQty === 0) {
		const removeEventData = { ...itemData, oldQty: current, action: 'remove' };
		if (!cartEmit('before-item-remove', removeEventData, true)) return;
		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
	} else if (newQty > current) {
		const eventData = { ...itemData, oldQty: current, newQty, max, action: 'increase' };
		if (!cartEmit('before-quantity-increase', eventData, true)) return;
		Cart.setQuantity(lineIndex, newQty);
		cartEmit('quantity-increased', eventData);
	} else {
		const eventData = { ...itemData, oldQty: current, newQty, action: 'decrease' };
		if (!cartEmit('before-quantity-decrease', eventData, true)) return;
		Cart.setQuantity(lineIndex, newQty);
		cartEmit('quantity-decreased', eventData);
	}
	debouncedUpdate(lineIndex);
};

/**
 * Removes an entire pack from the cart (all lines sharing the same _pack_uid),
 * plus any warranty lines rattached to that pack via _parent_id == packUid.
 *
 * Sequential /cart/change.js calls keyed by line_item.key — atomic per line,
 * avoids the edge case where the same variant exists outside the pack (safer
 * than a keyed cart/update.js by variant_id).
 *
 * Emits `cart:quantity-synced` at the end to trigger the section refresh
 * pipeline in mm_cart_page.js._bindRefreshTriggers.
 */
window.mmRemovePack = async (packUid, lineKeys) => {
	const validKeys = Array.isArray(lineKeys) ? lineKeys.filter((k) => typeof k === 'string' && k.length > 0) : [];
	if (validKeys.length === 0) {
		window.log('warn', '🛒 [CART_PACK]', 'No line keys to remove', { packUid });
		return;
	}

	try {
		// Fetch cart pour recuperer les warranty lines rattachees au pack (via _parent_id).
		// Ajout de leurs line.key aux keys a supprimer pour un cleanup en une seule sequence.
		const keysToRemove = [...validKeys];
		try {
			const cart = await CartAPI.request('GET', 'cart.js');
			const warrantyLines = cart.items.filter((i) =>
				i.properties && String(i.properties._parent_id) === String(packUid)
			);
			warrantyLines.forEach((wl) => {
				if (wl.key && !keysToRemove.includes(wl.key)) keysToRemove.push(wl.key);
			});
		} catch (fetchErr) {
			// Non bloquant : on continue meme si le fetch a echoue.
			window.log('warn', '🛒 [CART_PACK]', 'Warranty lookup failed, proceeding with pack keys only', { error: fetchErr.message });
		}

		for (const key of keysToRemove) {
			await CartAPI.request('POST', 'cart/change.js', { id: key, quantity: 0 });
		}
		cartEmit('quantity-synced', { lineIndex: 0, quantity: 0, variantId: null, packUid });
		window.log('info', '🛒 [CART_PACK]', 'Pack removed', { packUid, lines: keysToRemove.length });
	} catch (error) {
		window.log('error', '🛒 [CART_PACK]', 'Pack removal failed', { packUid, error: error.message });
	}
};

window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);

// Expose CartAPI : reutilise par des modules externes (ex: mm_pack_funnel.js) pour
// ajouter plusieurs items en un seul POST /cart/add.js sans dupliquer fetch/timeout/retry.
window.CartAPI = CartAPI;

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Refus persistant du cadeau : si l'item retire est un produit offert eligible en mode 'auto'
	// (present dans [data-free-products] avec data-free-product="true"), on memorise le refus
	// pour empecher processFreeProducts de le reajouter automatiquement.
	// En mode 'multi' (choix via slider), retirer un cadeau = simple deselection : pas de refus
	// persistant, l'utilisateur peut re-choisir librement dans le slider.
	document.addEventListener('cart:item-removed', (e) => {
		const variantId = e.detail?.data?.variantId;
		if (!variantId) return;
		const freeEl = document.querySelector(
			`${CART_CONFIG.selectors.freeProducts} [data-variant-id="${variantId}"]`
		);
		if (freeEl && freeEl.dataset.freeProduct === 'true' && freeEl.dataset.fpMode !== 'multi') {
			Cart.declineFreeProduct(variantId);
			window.log('info', 'Cart', 'Cadeau refuse', { variantId });
		}
	});

	// Close cart functionality
	document.addEventListener('click', (e) => {
		// Ouverture du panneau cadeau lateral (bouton "Choisir mon cadeau").
		const giftOpen = e.target.closest('[data-gift-panel-open]');
		if (giftOpen) {
			e.preventDefault();
			Cart.openGiftPanel(giftOpen.getAttribute('data-gift-panel-open'), giftOpen);
			return;
		}

		// Retour depuis le panneau cadeau (bouton fleche) : ferme le panneau, pas le panier.
		if (e.target.closest('[data-gift-panel-close]')) {
			e.preventDefault();
			Cart.closeGiftPanel();
			return;
		}

		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest('[data-checkout-btn]')) {
			const btn = e.target.closest('[data-checkout-btn]');
			btn.classList.add('mm-loading');
			cartEmit('checkout-loading', { element: btn });
		}

		// Discount removal
		const discountButton = e.target.closest('[data-discount-remove]');
		if (discountButton) {
			e.preventDefault();
			const discountTitle = discountButton.getAttribute('data-discount-title');
			if (discountTitle) {
				window.removeDiscount(discountTitle);
			}
		}
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
// Delegation globale : un seul listener 'submit' sur document, filtre sur
// [data-type="add-to-cart-form"]. Couvre automatiquement les formulaires injectes
// apres coup (cross-sell dynamique, recommandations, sections rechargees) sans avoir a
// rattacher de listener. Idempotent : le flag empeche un double binding si initAddToCartForms
// est rappelee (refreshDOM, mm_template_collection, init). Ne plus attacher de listener
// par formulaire.
window.initAddToCartForms = () => {
	if (document.documentElement.dataset.mmAtcDelegated === 'true') return;
	document.documentElement.dataset.mmAtcDelegated = 'true';

	document.addEventListener('submit', async (e) => {
		const form = e.target;
		if (!(form instanceof HTMLFormElement)) return;
		if (!form.matches('[data-type="add-to-cart-form"]')) return;

		e.preventDefault();

		const submitButton = form.querySelector('[type="submit"]');

		// Prevent submission if button is disabled (out of stock)
		if (submitButton && submitButton.disabled) {
			window.log('info', 'Cart', 'Add to cart blocked: product unavailable');
			return;
		}

		const formData = new FormData(form);

		if (!formData.get('id')) {
			window.log('error', 'Cart', 'No variant ID found in form', { form });
			return;
		}

		// Accessoire optionnel (switch mm-form-control name="accessory") : jamais un champ
		// cart/add.js reconnu, on le sort du FormData avant envoi et on l'ajoute a part.
		const accessoryInput = form.querySelector('.mm-form-control__input[name="accessory"]');
		const accessoryVariantId = (accessoryInput && accessoryInput.checked) ? accessoryInput.value : null;
		formData.delete('accessory');

		// Add loading state
		if (submitButton) {
			submitButton.classList.add('mm-loading');
			submitButton.disabled = true;
		}

		// Extract all form data including properties and options
		const extractedData = {
			variantId: formData.get('id'),
			quantity: parseInt(formData.get('quantity') || 1, 10),
			properties: {},
			options: {},
			formEntries: {}
		};

		// Collect all form entries
		for (const [key, value] of formData.entries()) {
			extractedData.formEntries[key] = value;

			// Extract properties (custom fields)
			if (key.startsWith('properties[')) {
				const propName = key.match(/properties\[(.+)\]/)?.[1];
				if (propName && value) {
					extractedData.properties[propName] = value;
				}
			}

			// Extract options (variant selections like size, color)
			if (key.startsWith('option')) {
				extractedData.options[key] = value;
			}
		}

		cartEmit('add-to-cart-start', extractedData);

		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

			// Add sections for cart update if available
			if (sectionId) {
				formData.append('sections', sectionId);
			}

			const response = await CartAPI.request('POST', 'cart/add.js', formData);

			// Accessoire coché : second ajout séquentiel (cart/add.js n'accepte qu'un item
			// par requête FormData). On refait un appel dédié pour recevoir des sections
			// à jour incluant l'accessoire, avant d'émettre le succès et d'ouvrir le panier.
			let accessoryResponse = null;
			if (accessoryVariantId) {
				const accessoryData = new FormData();
				accessoryData.append('id', accessoryVariantId);
				accessoryData.append('quantity', 1);
				if (sectionId) accessoryData.append('sections', sectionId);
				try {
					accessoryResponse = await CartAPI.request('POST', 'cart/add.js', accessoryData);
				} catch (error) {
					window.log('error', 'Cart', 'Add accessory to cart failed', { error: error.message, accessoryVariantId });
				}
			}

			// Combine les 2 responses dans un items[] quand un accessoire est ajoute — sinon
			// finalResponse = accessoryResponse ecrasait le produit principal et le toast
			// n'affichait que l'accessoire.
			const finalResponse = accessoryResponse
				? { items: [response, accessoryResponse] }
				: response;
			cartEmit('add-to-cart-success', {
				...extractedData,
				response: finalResponse
			});

			// Process free products after adding to cart
			await Cart.processFreeProducts(finalResponse);

			// Le drawer n'est plus ouvert automatiquement : le toast (mm_cart_toast.js) prend le relais
			// en s'abonnant a cart:add-to-cart-success et pointe vers la page /cart via son lien.

		} catch (error) {
			cartEmit('add-to-cart-error', {
				...extractedData,
				error: error.message
			});
			window.log('error', 'Cart', 'Add to cart failed', {
				error: error.message,
				...extractedData
			});
		} finally {
			// Remove loading state
			if (submitButton) {
				submitButton.classList.remove('mm-loading');
				submitButton.disabled = false;
			}
		}
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager?.loadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager?.loadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.LoaderManager?.loadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.LoaderManager?.loadThis(btn);
			}
		});
	});

	// Cart remove pack buttons (delete all lines sharing the same _pack_uid).
	// data-pack-line-keys : JSON stringified array of line_item.key values.
	document.querySelectorAll('[data-cart-remove-pack]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const packUid = btn.dataset.cartRemovePack;
			let lineKeys = [];
			try {
				lineKeys = JSON.parse(btn.dataset.packLineKeys || '[]');
			} catch (err) {
				window.log('error', '🛒 [CART_PACK]', 'Invalid data-pack-line-keys', { packUid, error: err.message });
				return;
			}
			window.mmRemovePack(packUid, lineKeys);
			window.LoaderManager?.loadThis(btn.closest('.mm-cart-pack') || btn);
		});
	});
}

/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */
// Delegation sur document : le bloc promo est rendu 2 fois sur /cart (aside desktop +
// .mm-cart-page__mobile-bottom) et ses noeuds sont remplaces a chaque refresh de section.
// Chaque action agit sur le bloc [data-promo] qui contient l'element declencheur.
function initPromoCode() {
	if (document.documentElement.dataset.promoHandlerAttached) return;
	document.documentElement.dataset.promoHandlerAttached = 'true';

	document.addEventListener('click', (e) => {
		const applyBtn = e.target.closest('[data-action="apply-discount"]');
		if (!applyBtn) return;
		e.preventDefault();
		applyDiscount(applyBtn.closest('[data-promo]'));
	});
	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Enter' || !e.target.matches('input[data-name="discount"]')) return;
		e.preventDefault();
		applyDiscount(e.target.closest('[data-promo]'));
	});
	// Hide error when user starts typing
	document.addEventListener('input', (e) => {
		if (e.target.matches('input[data-name="discount"]')) hidePromoError(e.target.closest('[data-promo]'));
	});
	window.log('info', 'Cart', 'Promo code listeners attached');
}

async function applyDiscount(container) {
	const input = container?.querySelector('input[data-name="discount"]');
	const button = container?.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		window.log('error', 'Cart', 'Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	window.log('info', 'Cart', 'Discount code entered', { code });

	if (!code) {
		window.log('warn', 'Cart', 'Empty discount code');
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		window.log('info', 'Cart', 'Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		// discount_codes = codes saisis uniquement (les reductions automatiques n'y sont pas,
		// contrairement a cart_level_discount_applications qui bloquait tout code saisi).
		const codesBefore = (cartBefore.discount_codes || []).filter(d => d.applicable !== false);
		window.log('info', 'Cart', 'Cart before', { total: cartBefore.total_price, codes: codesBefore });

		// Check if this exact code is already applied
		if (codesBefore.some(d => d.code && d.code.toLowerCase() === code.toLowerCase())) {
			showPromoError(container, input.dataset.errorAlreadyApplied || 'Code already applied');
			window.log('info', 'Cart', 'This code is already applied', { code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if another code is already applied (un seul code a la fois)
		if (codesBefore.length > 0) {
			const existingDiscount = codesBefore[0].code;
			showPromoError(container, `${input.dataset.errorAlreadyApplied || 'Code already applied'} (${existingDiscount})`);
			window.log('info', 'Cart', 'Another discount already applied', { existing: existingDiscount, attempted: code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			showPromoError(container, input.dataset.errorEmptyCart || 'Cart is empty');
			window.log('info', 'Cart', 'Cart is empty');
			input.value = ''; // Clear invalid code
			return;
		}

		// Apply discount via cart/update.js API
		window.log('info', 'Cart', 'Applying discount via API...', { code });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = {
			discount: code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload),
			signal: AbortSignal.timeout(CART_CONFIG.apiTimeout)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Cart updated', { updateData });

		// Verifier si le discount a ete applique — via `discount_codes` qui est la source
		// de verite Shopify (shape stable). L'ancien check `cart_level_discount_applications`
		// est instable : quand la request contient `sections`, Shopify wrap l'objet dans
		// `{ amount, discount_application: {...} }` au lieu de mettre `title` a la racine,
		// ce qui faisait echouer le check et declenchait un cleanup abusif du code valide.
		const codeApplied = updateData.discount_codes?.some(d => {
			return d.code && d.code.toLowerCase() === code.toLowerCase() && d.applicable !== false;
		});

		if (!codeApplied) {
			// Discount was not applied - code is invalid
			window.log('warn', 'Cart', 'Discount was not applied by Shopify', { code });

			// IMPORTANT: Force removal of invalid code from cart
			const cleanupPayload = {
				discount: '' // Empty string removes the discount
			};

			if (sectionId) {
				cleanupPayload.sections = sectionId;
			}

			try {
				await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(cleanupPayload),
					signal: AbortSignal.timeout(CART_CONFIG.apiTimeout)
				});
				window.log('info', 'Cart', 'Invalid code cleaned from cart', { code });
			} catch (cleanupError) {
				window.log('error', 'Cart', 'Failed to clean invalid code', { error: cleanupError.message });
			}

			showPromoError(container, input.dataset.errorNotApplicable || 'Discount code not applicable');
			input.value = ''; // Clear invalid code
			return;
		}

		// Discount applied successfully, refresh UI
		window.log('info', 'Cart', 'Discount applied successfully');

		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Clear input
		input.value = '';

		// Emit success event
		cartEmit('discount-applied', { code });
		window.log('info', 'Cart', 'Discount applied and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError(container, input.dataset.errorText || 'Error applying discount code');
		input.value = ''; // Clear invalid code on error
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(container, message) {
	const errorElement = container?.querySelector('[data-promo-error]');
	const input = container?.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.textContent = message;
		errorElement.classList.remove('mm-none');
	}

	if (input) {
		input.classList.add('error');
		input.setAttribute('aria-invalid', 'true');
	}

	// Auto-hide after 5 seconds
	clearTimeout(container?._promoErrorTimer);
	if (container) container._promoErrorTimer = setTimeout(() => hidePromoError(container), 5000);
}

function hidePromoError(container) {
	const errorElement = container?.querySelector('[data-promo-error]');
	const input = container?.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		errorElement.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
		input.removeAttribute('aria-invalid');
	}
}

window.removeDiscount = async function(code) {
	window.log('info', 'Cart', 'Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		window.log('info', 'Cart', 'Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload),
			signal: AbortSignal.timeout(CART_CONFIG.apiTimeout)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Discount removal response', { updateData });

		// Verifier la suppression directement depuis la reponse du POST (pas de GET cart.js redondant)
		const discountStillPresent = updateData.discount_codes?.some(d => {
			return d.code && d.code.toLowerCase() === code.toLowerCase();
		});

		if (discountStillPresent) {
			window.log('error', 'Cart', 'Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		window.log('info', 'Cart', 'Discount removed successfully', { code });

		// Refresh UI
		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		window.log('info', 'Cart', 'Discount removed and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	cartEmit('initializing');
	window.initCartQuantityListeners();
	initPromoCode();

	Cart.get()
		.then(data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
			initCartEventListeners();
			window.initAddToCartForms();
		})
		.catch(error => {
			window.log('error', 'Cart', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
});

